import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
} from 'react';
import { getInboxMetrics } from '../services/InboxService';
import { useSettings } from './SettingsContext';

/**
 * InboxContext — provides current inbox metrics for the sidebar badge and
 * filter chip counts. One source of truth keeps the badge and any filter
 * UI in sync without per-component polling.
 *
 * Cadence: a 60-second background refresh matches our actual intake
 * pace. Polling more aggressively hammers the backend for almost no
 * UX benefit since invoice arrivals are sparse. Components that mutate
 * state (dismiss / retry) should call `refreshMetrics()` directly
 * instead of waiting for the next tick.
 */

const DEFAULT_METRICS = {
    new: 0,
    processing: 0,
    awaiting_approval: 0,
    linked: 0,
    dismissed: 0,
    error: 0,
    done: 0,
    total: 0,
};

const REFRESH_INTERVAL_MS = 60_000;

const InboxContext = createContext({
    metrics: DEFAULT_METRICS,
    isLoading: false,
    error: null,
    refreshMetrics: () => {},
});

export const InboxProvider = ({ children }) => {
    const { companyId } = useSettings();
    const [metrics, setMetrics] = useState(DEFAULT_METRICS);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    const refreshMetrics = useCallback(async () => {
        if (!companyId) return;
        try {
            setIsLoading(true);
            const data = await getInboxMetrics(companyId);
            if (mountedRef.current) {
                setMetrics({ ...DEFAULT_METRICS, ...data });
                setError(null);
            }
        } catch (err) {
            // Surface only structural errors; transient network blips are
            // best ignored so a stale-but-numeric badge doesn't disappear.
            if (mountedRef.current) {
                setError(err);
            }
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [companyId]);

    // Initial load + interval on companyId change. Cleanup intervals on
    // unmount / companyId switch so we don't leak timers across tenant hops.
    useEffect(() => {
        mountedRef.current = true;
        if (!companyId) {
            setMetrics(DEFAULT_METRICS);
            return undefined;
        }
        refreshMetrics();
        const id = setInterval(refreshMetrics, REFRESH_INTERVAL_MS);
        return () => {
            mountedRef.current = false;
            clearInterval(id);
        };
    }, [companyId, refreshMetrics]);

    return (
        <InboxContext.Provider value={{ metrics, isLoading, error, refreshMetrics }}>
            {children}
        </InboxContext.Provider>
    );
};

export const useInbox = () => useContext(InboxContext);

// Derived helper: the count we surface as the sidebar badge.
// "Items that need user attention" = new + awaiting_approval + error.
// Processing is intentionally excluded (transient — flickers).
export const getUnhandledCount = (metrics) =>
    (metrics?.new || 0) + (metrics?.awaiting_approval || 0) + (metrics?.error || 0);
